'use client'

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import { motion, stagger, AnimatePresence } from 'motion/react'
import { ComponentProps, Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

type Props<T> = {
  canRenderExpandedRow?(row: T): boolean
  columns: CustomColumnDef<T>[]
  data: T[]
  getRowProps?(row: T): ComponentProps<typeof TableRow>
  renderExpandedRow?(row: T): React.ReactNode
  showColumnsFilter?: boolean
  showSearchFilter?: boolean
}

export type CustomColumnDef<T> = ColumnDef<T> & {
  columnClassName?: string
}

const DataTable = <T,>({
  canRenderExpandedRow,
  columns: _columns,
  data,
  getRowProps,
  renderExpandedRow,
  showColumnsFilter = false,
  showSearchFilter = false,
}: Props<T>) => {
  // Default column ids to their accessorKey if not provided, so we can match them when getting column class names and such
  const columns = useMemo(
    () => _columns.map(col => ({ ...col, id: col.id ?? (col as any).accessorKey })),
    [_columns],
  )
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowCanExpand: data => canRenderExpandedRow?.(data.original) ?? false,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    state: { columnFilters, columnVisibility, sorting },
  })
  const { t } = useTranslation()

  const getColumnClassName = (columnId: string) => {
    return columns.find(col => col.id === columnId)?.columnClassName
  }

  const isShowingHeader = showColumnsFilter || showSearchFilter

  return (
    <div>
      {isShowingHeader && (
        <div className="flex items-center pb-4">
          {showSearchFilter && (
            <Input
              className="max-w-sm"
              onChange={event => table.getColumn('name')?.setFilterValue(event.target.value)}
              placeholder={`${t('global.search')}...`}
              value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
            />
          )}
          {showColumnsFilter && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="ml-auto" variant="outline">
                  {t('global.columns')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter(column => column.getCanHide())
                  .map(column => {
                    return (
                      <DropdownMenuCheckboxItem
                        checked={column.getIsVisible()}
                        className="capitalize"
                        key={column.id}
                        onCheckedChange={value => column.toggleVisibility(!!value)}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      <div className="@container/table overflow-hidden rounded-md bg-layer-1 border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => {
                  return (
                    <TableHead
                      className={cn(getColumnClassName(header.id), {
                        'pr-0': i === 0,
                      })}
                      key={header.id}
                      style={{ width: header.column.columnDef.size }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <AnimatePresence>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map(row => {
                  const rowProps = getRowProps?.(row.original) ?? {}

                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        asChild
                        data-expandable={row.getCanExpand()}
                        data-state={row.getIsSelected() && 'selected'}
                        onClick={() => {
                          if (row.getCanExpand()) row.toggleExpanded()
                        }}
                        {...rowProps}
                        className={cn(
                          'group',
                          {
                            'border-l-4 border-l-focus bg-layer-2': row.getIsExpanded(),
                            'cursor-pointer': row.getCanExpand(),
                          },
                          rowProps.className,
                        )}
                      >
                        <motion.tr>
                          {row.getVisibleCells().map((cell, i) => (
                            <TableCell
                              className={cn(getColumnClassName(cell.column.id), {
                                'pr-0': i === 0,
                              })}
                              key={cell.id}
                              style={{ width: cell.column.columnDef.size }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </motion.tr>
                      </TableRow>
                      {row.getIsExpanded() && typeof renderExpandedRow !== 'undefined' && (
                        <TableRow className="border-l-4 border-l-focus">
                          <TableCell colSpan={row.getVisibleCells().length}>
                            {renderExpandedRow?.(row.original)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell className="h-24 text-center" colSpan={columns.length}>
                    {t('global.noResults')}
                  </TableCell>
                </TableRow>
              )}
            </AnimatePresence>
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

const DataTableSkeleton = () => {
  return (
    <motion.div
      animate="show"
      className="flex flex-col gap-4"
      exit="exit"
      initial="hidden"
      variants={{
        hidden: {},
        show: {
          transition: { delayChildren: stagger(0.05) },
        },
      }}
    >
      <Skeleton
        className="max-w-sm h-12"
        variants={{
          hidden: { opacity: 0, y: 40 },
          show: {
            opacity: 1,
            transition: {
              duration: 0.18,
              ease: 'easeOut',
            },
            y: 0,
          },
        }}
      />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton
          className="w-full h-20"
          key={i}
          variants={{
            hidden: { opacity: 0, y: 40 },
            show: {
              opacity: 1,
              transition: {
                duration: 0.18,
                ease: 'easeOut',
              },
              y: 0,
            },
          }}
        />
      ))}
    </motion.div>
  )
}

export default DataTable

export { DataTableSkeleton }
